"use client"

import { Sun, Moon } from 'lucide-react'
import { useTheme } from 'next-themes'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslations } from "next-intl"

type ButtonProps = React.ComponentProps<typeof Button>

/**
 * `size` is a prop so the toggle can match whatever row it sits in — the app
 * header's controls are `icon`, the marketing header's were `icon-sm`. Without
 * it the one shared header ends up with one button a size off from its
 * neighbours, which reads as a misalignment bug.
 */
export function ThemeToggle({
  size = 'icon-sm',
  variant = 'secondary',
}: {
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
}) {
  const t = useTranslations("nav")
  const { resolvedTheme, setTheme } = useTheme()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          className="rounded-full"
          aria-label={t("toggleTheme")}
        >
          <Sun className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <Moon className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {resolvedTheme === "light" ? t("switchToDarkMode") : t("switchToLightMode")}
      </TooltipContent>
    </Tooltip>
  )
}
